import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../components/layout/MainLayout";
import Breadcrumbs from "../components/common/Breadcrumbs";
import Swal from "sweetalert2";
import { selectHasPermission } from "../store/slices/authSlice";
import SessionTypeList from "../pages/SessionType/SessionTypeList";
import { getApiErrorMessage } from "../utils/apiError";
import {
    fetchSessionMasters,
    deleteSessionMaster,
    clearError,
} from "../store/slices/sessionSlice";

const SessionTypes = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const canManageSessionTypes = useSelector((state) =>
        selectHasPermission(state, "session-types-manage")
    );
    const canCreateSessionTypes = useSelector((state) =>
        selectHasPermission(state, "session-types-create")
    );
    const canEditSessionTypes = useSelector((state) =>
        selectHasPermission(state, "session-types-edit")
    );
    const canDeleteSessionTypes = useSelector((state) =>
        selectHasPermission(state, "session-types-delete")
    );

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setIsLoading(true);
        dispatch(fetchSessionMasters()).finally(() => setIsLoading(false));
    }, [dispatch]);

    const handleAddSessionType = () => navigate("/session-types/add");
    const handleEditSessionType = (id) => navigate(`/session-types/edit/${id}`);
    const handleDeleteSessionType = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You want to delete this session type? This might affect sub-sessions.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteSessionMaster(id)).unwrap();
                Swal.fire("Deleted!", "Session Type has been deleted.", "success");
            } catch (err) {
                Swal.fire("Error!", getApiErrorMessage(err, "Failed to delete session type."), "error");
            }
        }
    };

    if (!canManageSessionTypes) {
        return (
            <Navigate
                to="/dashboard"
                state={{ error: "You do not have permission to manage session types." }}
                replace
            />
        );
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />

                <div className="card">
                    <div className="card-body">
                        {isLoading ? (
                            <div className="d-flex justify-content-center py-5">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <SessionTypeList
                                onEditSessionType={canEditSessionTypes ? handleEditSessionType : null}
                                onDeleteSessionType={canDeleteSessionTypes ? handleDeleteSessionType : null}
                                onAddSessionType={canCreateSessionTypes ? handleAddSessionType : null}
                            />
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default SessionTypes;
